import React, { useState, useMemo } from 'react';
import { useGetAllPublishedBlogsQuery, AllPublishedBlog } from '../store/topicApi';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const categoryColor = (cat?: string) => {
  const map: Record<string, string> = {
    QA: 'bg-emerald-500',
    DevOps: 'bg-trident-orange',
    'AI/ML': 'bg-purple-500',
    Development: 'bg-blue-500',
    Security: 'bg-red-500',
    Architecture: 'bg-sky-500',
    'Power Platform': 'bg-indigo-500',
    'Business Analysis': 'bg-amber-500',
  };
  return map[cat || ''] || 'bg-trident-gray-400';
};

function parsePublishedDate(dateStr?: string): Date | null {
  if (!dateStr) return null;
  // Formats: "13-Apr-2026" OR "Apr 13, 2026"
  let d = new Date(dateStr);
  if (!isNaN(d.getTime())) return d;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    const [day, mon, year] = parts;
    const monIdx = months.indexOf(mon);
    if (monIdx >= 0) return new Date(parseInt(year), monIdx, parseInt(day));
  }
  return null;
}

const ContentCalendar: React.FC = () => {
  const { data: blogs = [], isLoading, error } = useGetAllPublishedBlogsQuery();
  const today = new Date();
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  // Group blogs by yyyy-mm-dd
  const blogsByDate = useMemo(() => {
    const map: Record<string, AllPublishedBlog[]> = {};
    blogs.forEach(b => {
      const d = parsePublishedDate(b.publishedDate);
      if (!d) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      if (!map[key]) map[key] = [];
      map[key].push(b);
    });
    return map;
  }, [blogs]);

  const goPrevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
    setSelectedDay(null);
  };
  const goNextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
    setSelectedDay(null);
  };
  const goToday = () => {
    setViewMonth(today.getMonth());
    setViewYear(today.getFullYear());
    setSelectedDay(null);
  };

  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells: Array<{ date: Date | null }> = [];
  for (let i = 0; i < firstDay; i++) cells.push({ date: null });
  for (let i = 1; i <= daysInMonth; i++) cells.push({ date: new Date(viewYear, viewMonth, i) });

  const selectedKey = selectedDay
    ? `${selectedDay.getFullYear()}-${String(selectedDay.getMonth() + 1).padStart(2, '0')}-${String(selectedDay.getDate()).padStart(2, '0')}`
    : null;
  const selectedBlogs = selectedKey ? blogsByDate[selectedKey] || [] : [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-trident-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return <div className="text-center py-10 text-red-500 font-semibold">Failed to load calendar.</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="font-display font-bold text-2xl text-trident-gray-900">Content Calendar</h2>
        <p className="text-sm text-trident-gray-500">
          Month view of when blogs were published. Click a day to see details.
        </p>
      </div>

      <div className="bg-white rounded-2xl border border-trident-gray-100 shadow-card overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 border-b border-trident-gray-100">
          <h3 className="font-display font-bold text-xl text-trident-gray-900">
            {MONTH_NAMES[viewMonth]} {viewYear}
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={goToday} className="btn-secondary text-sm py-2 px-4">Today</button>
            <button onClick={goPrevMonth} className="p-2 rounded-lg border border-trident-gray-200 hover:border-trident-orange hover:text-trident-orange transition">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
            <button onClick={goNextMonth} className="p-2 rounded-lg border border-trident-gray-200 hover:border-trident-orange hover:text-trident-orange transition">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 border-b border-trident-gray-100 bg-trident-gray-50">
          {DAY_NAMES.map(d => (
            <div key={d} className="py-2 text-center text-xs font-display font-bold uppercase tracking-wider text-trident-gray-500">
              {d}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {cells.map((cell, i) => {
            if (!cell.date) {
              return <div key={i} className="min-h-[90px] border-b border-r border-trident-gray-50 bg-trident-gray-50/30" />;
            }
            const key = `${cell.date.getFullYear()}-${String(cell.date.getMonth() + 1).padStart(2, '0')}-${String(cell.date.getDate()).padStart(2, '0')}`;
            const dayBlogs = blogsByDate[key] || [];
            const isToday = cell.date.toDateString() === today.toDateString();
            const isSelected = selectedDay && cell.date.toDateString() === selectedDay.toDateString();

            return (
              <button
                key={i}
                onClick={() => setSelectedDay(cell.date)}
                className={`min-h-[90px] border-b border-r border-trident-gray-50 p-2 text-left hover:bg-trident-orange-light/40 transition
                  ${isSelected ? 'bg-trident-orange-light ring-2 ring-inset ring-trident-orange' : ''}
                  ${isToday ? 'bg-trident-orange-light/30' : ''}`}
              >
                <div className={`text-xs font-display font-bold mb-1 inline-flex items-center justify-center w-6 h-6 rounded-full
                  ${isToday ? 'bg-trident-gradient text-white shadow-trident' : 'text-trident-gray-700'}`}>
                  {cell.date.getDate()}
                </div>
                <div className="space-y-0.5">
                  {dayBlogs.slice(0, 2).map((b, j) => (
                    <div key={j} className="flex items-center gap-1 text-[10px] leading-tight">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${categoryColor(b.category)}`} />
                      <span className="truncate font-semibold text-trident-gray-700">{b.title}</span>
                    </div>
                  ))}
                  {dayBlogs.length > 2 && (
                    <div className="text-[10px] text-trident-orange font-display font-bold">+{dayBlogs.length - 2} more</div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {selectedDay && (
        <div className="mt-6 bg-white rounded-2xl border border-trident-gray-100 shadow-card p-5 animate-slide-up">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg text-trident-gray-900">
              {selectedDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </h3>
            <button onClick={() => setSelectedDay(null)} className="text-trident-gray-400 hover:text-trident-gray-700">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          {selectedBlogs.length === 0 ? (
            <p className="text-sm text-trident-gray-500 text-center py-6">No blogs published on this day.</p>
          ) : (
            <div className="space-y-2">
              {selectedBlogs.map(b => (
                <a
                  key={b._id}
                  href={b.publishedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl border border-trident-gray-100 hover:border-trident-orange/40 hover:bg-trident-orange-light/30 transition group"
                >
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${categoryColor(b.category)}`} />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm text-trident-gray-900 truncate">{b.title}</div>
                    <div className="text-xs text-trident-gray-500">{b.authorName} · {b.category || 'Uncategorized'}</div>
                  </div>
                  <svg className="text-trident-gray-400 group-hover:text-trident-orange transition" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContentCalendar;
